import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { on as wsOn } from "@/lib/socket";
import StatusBadge from "@/components/ticket/StatusBadge";
import PriorityBadge from "@/components/ticket/PriorityBadge";
import SLACountdown from "@/components/ticket/SLACountdown";
import { Search } from "lucide-react";

const STATUS_TABS = [
    { key: "ALL", label: "All" },
    { key: "OPEN", label: "Open" },
    { key: "IN_PROGRESS", label: "In Progress" },
    { key: "WAITING_CLIENT", label: "Waiting Client" },
    { key: "RESOLVED", label: "Resolved" },
    { key: "CLOSED", label: "Closed" },
    { key: "ESCALATED", label: "Escalated" },
    { key: "REOPENED", label: "Reopened" },
];

export default function Tickets() {
    const [tickets, setTickets] = useState([]);
    const [statusTab, setStatusTab] = useState("ALL");
    const [priority, setPriority] = useState("");
    const [sla, setSla] = useState("");
    const [paid, setPaid] = useState("");
    const [q, setQ] = useState("");
    const [categories, setCategories] = useState([]);
    const [category, setCategory] = useState("");
    const [users, setUsers] = useState([]);
    const [assignee, setAssignee] = useState("");

    const load = () => {
        const params = {};
        if (statusTab !== "ALL") params.status = statusTab;
        if (priority) params.priority = priority;
        if (sla) params.sla = sla;
        if (paid) params.paid = paid === "paid";
        if (category) params.category_id = category;
        if (assignee) params.assigned_to = assignee;
        if (q) params.q = q;
        api.get("/tickets", { params }).then((r) => setTickets(r.data));
    };

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
        api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        load();
        const off1 = wsOn("ticket:created", load);
        const off2 = wsOn("ticket:updated", load);
        return () => {
            off1();
            off2();
        };
        // eslint-disable-next-line
    }, [statusTab, priority, sla, paid, category, assignee, q]);

    return (
        <div className="space-y-4" data-testid="tickets-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Tickets</h1>
                    <p className="text-xs text-muted-foreground">
                        {tickets.length} matching
                    </p>
                </div>
            </div>
            <div className="flex flex-wrap gap-1 border-b border-border">
                {STATUS_TABS.map((t) => (
                    <button
                        key={t.key}
                        data-testid={`tickets-tab-${t.key.toLowerCase()}`}
                        onClick={() => setStatusTab(t.key)}
                        className={`px-3 py-2 text-xs border-b-2 transition-colors ${
                            statusTab === t.key
                                ? "border-primary text-foreground"
                                : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            <div className="flex flex-wrap gap-2">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="size-3.5 absolute left-3 top-2.5 text-muted-foreground" />
                    <input
                        data-testid="tickets-search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search ticket, phone, name, company…"
                        className="w-full pl-9 pr-3 py-2 text-sm bg-card border border-border rounded-md focus:outline-none focus:border-primary"
                    />
                </div>
                <Select
                    testId="tickets-filter-priority"
                    value={priority}
                    onChange={setPriority}
                    options={[
                        { v: "", label: "All priorities" },
                        { v: "IMMEDIATE", label: "Immediate" },
                        { v: "PRIORITY", label: "Priority" },
                        { v: "STANDARD", label: "Standard" },
                    ]}
                />
                <Select
                    testId="tickets-filter-sla"
                    value={sla}
                    onChange={setSla}
                    options={[
                        { v: "", label: "Any SLA" },
                        { v: "on-track", label: "On track" },
                        { v: "at-risk", label: "At risk" },
                        { v: "breached", label: "Breached" },
                    ]}
                />
                <Select
                    testId="tickets-filter-paid"
                    value={paid}
                    onChange={setPaid}
                    options={[
                        { v: "", label: "Paid/Free" },
                        { v: "paid", label: "Paid only" },
                        { v: "free", label: "Free only" },
                    ]}
                />
                <Select
                    testId="tickets-filter-category"
                    value={category}
                    onChange={setCategory}
                    options={[{ v: "", label: "All categories" }, ...categories.map((c) => ({ v: c.id, label: c.name }))]}
                />
                <Select
                    testId="tickets-filter-assignee"
                    value={assignee}
                    onChange={setAssignee}
                    options={[{ v: "", label: "Any assignee" }, ...users.map((u) => ({ v: u.id, label: u.name }))]}
                />
            </div>

            <div className="bg-card border border-border rounded-md overflow-hidden">
                <div className="grid grid-cols-[100px_90px_1fr_140px_120px_120px_110px] gap-0 text-[10px] uppercase tracking-wider text-muted-foreground font-mono px-3 py-2 border-b border-border bg-[hsl(240,3%,6%)]">
                    <div>ID</div>
                    <div>Priority</div>
                    <div>Customer / Issue</div>
                    <div>Category</div>
                    <div>Assignee</div>
                    <div>Status</div>
                    <div>SLA</div>
                </div>
                <div className="divide-y divide-border">
                    {tickets.map((t) => (
                        <Link
                            key={t.id}
                            to={`/tickets/${t.id}`}
                            data-testid={`ticket-row-${t.ticket_number}`}
                            className="grid grid-cols-[100px_90px_1fr_140px_120px_120px_110px] items-center px-3 py-2 hover:bg-secondary/50 transition-colors text-sm"
                        >
                            <div className="font-mono text-primary text-xs">{t.ticket_number}</div>
                            <div><PriorityBadge value={t.priority_tier} /></div>
                            <div className="truncate pr-3">
                                <div className="truncate">{t.customer_name || t.customer_phone}</div>
                                <div className="text-[11px] text-muted-foreground truncate">
                                    {t.issue_description || "Details pending"}
                                </div>
                            </div>
                            <div className="text-xs text-muted-foreground truncate">{t.category_name || "—"}</div>
                            <div className="text-xs truncate">{t.assigned_to_name || "—"}</div>
                            <div><StatusBadge value={t.status} /></div>
                            <div><SLACountdown deadline={t.sla_deadline} status={t.status} compact /></div>
                        </Link>
                    ))}
                    {tickets.length === 0 && (
                        <div className="text-sm text-muted-foreground py-10 text-center">
                            No tickets match your filters. Use the WhatsApp simulator to create one.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Select({ value, onChange, options, testId }) {
    return (
        <select
            data-testid={testId}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="bg-card border border-border rounded-md px-3 py-2 text-xs focus:outline-none focus:border-primary"
        >
            {options.map((o) => (
                <option key={o.v} value={o.v}>{o.label}</option>
            ))}
        </select>
    );
}
